import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import WestIcon from "@mui/icons-material/West";
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as MenuItemField,
  Tooltip,
} from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Link } from "react-router-dom";
import MuiIcon from "../shared/icon/MuiIcon";

const hasMenuChildren = (menu) =>
  Array.isArray(menu?.child) && menu.child.length > 0;

const isMenuLinkActive = (menu, pathname) => {
  if (!menu?.link) return false;
  if (menu.link === "/") return menu.link === pathname;
  if (menu.level === 2 && !menu.controller) return false;
  return menu.level !== 2 && pathname === menu.link;
};

const menuContainsActivePath = (menu, pathname) => {
  if (isMenuLinkActive(menu, pathname)) return true;
  if (!hasMenuChildren(menu)) return false;
  return menu.child.some((child) => menuContainsActivePath(child, pathname));
};

const flattenMenuLeaves = (nodes, depth = 0, acc = []) => {
  if (!Array.isArray(nodes)) return acc;
  nodes.forEach((node) => {
    if (hasMenuChildren(node)) {
      acc.push({ node, depth, isGroup: true });
      flattenMenuLeaves(node.child, depth + 1, acc);
    } else {
      acc.push({ node, depth, isGroup: false });
    }
  });
  return acc;
};

const SubMenuBranch = ({
  submenu,
  depth = 0,
  pathname,
  isOpenedSideBar,
  onLeafClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const nested = hasMenuChildren(submenu);

  useEffect(() => {
    if (nested && menuContainsActivePath(submenu, pathname)) {
      setIsOpen(true);
    }
  }, [nested, pathname, submenu]);

  if (nested) {
    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            className={clsx({
              "submenu-item": true,
              "submenu-group-item": true,
              active: menuContainsActivePath(submenu, pathname),
            })}
            sx={{ pl: 2 + depth * 1.5 }}
            onClick={(event) => {
              event.preventDefault();
              setIsOpen((prev) => !prev);
            }}
          >
            <ListItemIcon sx={{ mx: 0, minWidth: "24px" }}>
              {submenu.icon ? <MuiIcon name={submenu.icon} /> : <WestIcon />}
            </ListItemIcon>
            <ListItemText
              primary={submenu.title}
              className="text"
              sx={{ whiteSpace: "nowrap", overflow: "hidden" }}
            />
            <ListItemIcon
              className={clsx({ "submenu-icon": true, rotate: isOpen })}
              sx={{ minWidth: "24px" }}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {submenu.child.map((child, index) => (
              <SubMenuBranch
                key={`${submenu.id}-${child.id || index}`}
                submenu={child}
                depth={depth + 1}
                pathname={pathname}
                isOpenedSideBar={isOpenedSideBar}
                onLeafClick={onLeafClick}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <Link
      to={submenu.link}
      onClick={() => {
        onLeafClick();
      }}
    >
      <ListItem disablePadding>
        <ListItemButton
          className={clsx({
            "submenu-item": true,
            active: isMenuLinkActive(submenu, pathname),
          })}
          sx={{ pl: 2 + depth * 1.5 }}
        >
          <ListItemIcon sx={{ mx: 0, minWidth: "24px" }}>
            <WestIcon />
          </ListItemIcon>
          <ListItemText
            primary={submenu.title}
            className="text"
            sx={{ whiteSpace: "nowrap", overflow: "hidden" }}
          />
        </ListItemButton>
      </ListItem>
    </Link>
  );
};

const MenuItem = ({ menu, isOpenedSideBar, setIsOpenedSideBar }) => {
  const location = useLocation();

  const [isTooltipOpened, setIsTooltipOpened] = useState(false);
  const [isShowSubMenu, setIsShowSubMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pathname, setPathname] = useState("");

  useEffect(() => {
    if (!isOpenedSideBar) {
      setIsShowSubMenu(false);
      setAnchorEl(null);
    }
  }, [isOpenedSideBar]);

  useEffect(() => {
    setPathname(location.pathname);
  }, [location]);

  useEffect(() => {
    if (hasMenuChildren(menu) && menuContainsActivePath(menu, pathname)) {
      setIsShowSubMenu(true);
    }
  }, [menu, pathname]);

  const onClickMenuItem = async (event, menuItem) => {
    if (hasMenuChildren(menuItem)) {
      event.preventDefault();
      setIsShowSubMenu(!isShowSubMenu);
      setAnchorEl(event.currentTarget);
    } else {
      setIsOpenedSideBar(false);
    }
  };

  const onCloseAnchorEl = () => {
    setIsShowSubMenu(false);
    setAnchorEl(null);
  };

  const onLeafClick = () => {
    setIsOpenedSideBar(false);
    onCloseAnchorEl();
  };

  const flatPopupItems = hasMenuChildren(menu)
    ? flattenMenuLeaves(menu.child)
    : [];

  return (
    <ListItem
      sx={{ display: "block", p: "0.25rem" }}
      className="menu-item-container"
    >
      <Tooltip
        placement="left"
        title={menu?.title}
        arrow
        open={isTooltipOpened}
        onOpen={() => {
          if (!isOpenedSideBar) {
            setIsTooltipOpened(true);
          }
        }}
        onClose={() => setIsTooltipOpened(false)}
      >
        <Link
          to={menu?.link}
          onClick={(event) => {
            onClickMenuItem(event, menu);
          }}
        >
          <ListItemButton
            className={clsx({
              "menu-item-button": true,
              active:
                menu?.link === "/"
                  ? menu?.link === pathname
                  : (menu.level !== 2 || menu.oldLink) &&
                    pathname.includes(
                      menu?.link + (menu.level < 2 ? "/" : "")
                    ),
              open: isOpenedSideBar,
            })}
            sx={{
              justifyContent: isOpenedSideBar ? "initial" : "center",
              px: 2,
            }}
            onClick={(event) => {
              onClickMenuItem(event, menu);
            }}
          >
            <ListItemIcon className="menu-icon">
              <MuiIcon name={menu?.icon} />
            </ListItemIcon>

            {isOpenedSideBar ? (
              <ListItemText
                primary={menu.title}
                className="text"
                sx={{ opacity: isOpenedSideBar ? 1 : 0 }}
              />
            ) : null}

            {hasMenuChildren(menu) ? (
              <ListItemIcon
                className={clsx({
                  "submenu-icon": true,
                  rotate: isShowSubMenu,
                })}
                sx={{
                  ml: isOpenedSideBar ? 3 : "auto",
                }}
              >
                <KeyboardArrowLeftIcon />
              </ListItemIcon>
            ) : null}
          </ListItemButton>
        </Link>
      </Tooltip>

      {hasMenuChildren(menu) ? (
        isOpenedSideBar ? (
          <Collapse
            in={isShowSubMenu && isOpenedSideBar}
            timeout="auto"
            unmountOnExit
            sx={{ mt: 0.5 }}
          >
            <List disablePadding className="submenu-list-container">
              {menu.child.map((submenu, index) => (
                <SubMenuBranch
                  key={`${menu.id}-submenu-${submenu.id || index}`}
                  submenu={submenu}
                  pathname={pathname}
                  isOpenedSideBar={isOpenedSideBar}
                  onLeafClick={onLeafClick}
                />
              ))}
            </List>
          </Collapse>
        ) : (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onCloseAnchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
          >
            {flatPopupItems.map(({ node, depth, isGroup }, index) =>
              isGroup ? (
                <MenuItemField
                  key={`${menu.id}-popup-group-${node.id || index}`}
                  disabled
                  sx={{ pl: 2 + depth * 2, opacity: 1, fontWeight: 600 }}
                >
                  {node.title}
                </MenuItemField>
              ) : (
                <Link
                  to={node.link}
                  onClick={onLeafClick}
                  key={`${menu.id}-popup-leaf-${node.id || index}`}
                >
                  <MenuItemField
                    sx={{
                      pl: 2 + depth * 2,
                      color:
                        isMenuLinkActive(node, pathname) ? "#fff" : "#555555",
                      background: isMenuLinkActive(node, pathname)
                        ? "#163ba2"
                        : "#fff",
                    }}
                  >
                    {node.title}
                  </MenuItemField>
                </Link>
              )
            )}
          </Menu>
        )
      ) : null}
    </ListItem>
  );
};

export default MenuItem;
